import { Box, Button, Modal, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import html2pdf from "html2pdf.js";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const Preview = ({ rfq, setStep, data, user }) => {
  const navigate = useNavigate();
  const vendorId = JSON.parse(localStorage.getItem("vendor"));
  const [loading, setLoading] = useState(false);
  const [currency, setCurrency] = useState();
  const [open, setOpen] = useState(false);
  const pdfRef = useRef();

  useEffect(() => {
    const getCurrency = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/org-currency?rfqId=${rfq.rfqId}`,
          { headers: { Authorization: `Bearer ${user.accessToken}` } }
        );
        setCurrency(res.data);
      } catch (error) {
        console.log(error);
      }
    };
    getCurrency();
  }, []);

  const handleExportPDF = () => {
    const pdfContainer = pdfRef.current;

    // Add a class for PDF-specific styling
    pdfContainer.classList.add("pdf-mode");

    html2pdf()
      .from(pdfContainer)
      .set({
        margin: 10,
        filename: "Revised_Quote_Submission.pdf",
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { format: "a4", orientation: "portrait" },
      })
      .save()
      .then(() => {
        // Remove the PDF-specific class after export
        pdfContainer.classList.remove("pdf-mode");
      });
  };

  const grandTotalsByCurrency = data.reduce((acc, row) => {
    const shortform =
      typeof row.currency === "string" ? row.currency : row.currency?.shortform;

    if (!shortform) return acc;

    const currencyObj = currency?.find(
      (c) => c.currency.shortform === shortform
    );

    const symbol = currencyObj?.currency?.symbol || shortform;

    if (!acc[shortform]) {
      acc[shortform] = { symbol, total: 0 };
    }

    acc[shortform].total += parseFloat(row.total);

    return acc;
  }, {});

  const groupedWorkItems = data.reduce((acc, item) => {
    if (!acc[item.section]) {
      acc[item.section] = [];
    }
    acc[item.section].push(item);
    return acc;
  }, {});

  const handleNext = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/bid`,
        {
          vendorId: vendorId,
          rfqId: rfq.rfqId,
          bidWorkItems: data,
        },
        { headers: { Authorization: `Bearer ${user.accessToken}` } }
      );
      navigate("/vendor/dashboard");
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep((prev) => prev - 1);
  };
  return (
    <div style={{ position: "relative" }}>
      <div style={{ display: "flex", justifyContent: "end" }}>
        <Button
          className="btn-contained"
          sx={{ width: "200px" }}
          onClick={handleExportPDF}
        >
          Export PDF
        </Button>
      </div>
      {loading && <div className="loading-div"></div>}
      <h2>Quotation Submission Review</h2>
      <Box className="dashboard-container" px={10} ref={pdfRef}>
        <Typography className="form-title">Work Item Details</Typography>
        <table
          border={1}
          style={{
            borderCollapse: "collapse",
            width: "100%",
            marginTop: "10px",
          }}
        >
          <thead>
            <tr>
              <th style={{ padding: "20px" }}>Item Code</th>
              <th>Description</th>
              <th>Item Group</th>
              <th>UOM</th>
              <th>Quantity</th>
              <th>Currency</th>
              <th>Rate</th>
              <th>GST(in %)</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {groupedWorkItems &&
              Object.entries(groupedWorkItems).map(
                ([section, items], sectionIndex) => {
                  const sectionTotal = items
                    .reduce((acc, item) => acc + parseFloat(item.total), 0)
                    .toFixed(2);
                  return (
                    <React.Fragment key={sectionIndex}>
                      <tr
                        style={{
                          backgroundColor: "#d9edf7",
                          fontWeight: "bold",
                        }}
                      >
                        <td
                          colSpan={8}
                          style={{ textAlign: "left", padding: "10px" }}
                        >
                          {section}
                        </td>
                        <td style={{ textAlign: "center", padding: "10px" }}>
                          {sectionTotal &&
                            new Intl.NumberFormat("en-IN", {
                              style: "decimal",
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(sectionTotal)}
                        </td>
                      </tr>
                      {items.map((item, index) => (
                        <tr key={index} style={{ textAlign: "center" }}>
                          <td style={{ padding: "20px" }}>{item.code}</td>
                          <td>{item.description}</td>
                          <td>{item.section}</td>
                          <td>{item.unit}</td>
                          <td>{item.quantity}</td>
                          <td>{item.currency}</td>
                          <td>
                            {new Intl.NumberFormat("en-IN", {
                              style: "decimal",
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(item.rate)}
                          </td>
                          <td>{item.gst}</td>
                          <td>
                            {new Intl.NumberFormat("en-IN", {
                              style: "decimal",
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(item.total)}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                }
              )}
            {Object.entries(grandTotalsByCurrency).map(
              ([shortform, { symbol, total }]) => (
                <tr
                  key={shortform}
                  style={{ backgroundColor: "#d8e9f0", fontWeight: "bold" }}
                >
                  <td
                    colSpan={8}
                    style={{ textAlign: "right", padding: "20px" }}
                  >
                    Grand Total (of {symbol}):
                  </td>
                  <td style={{ width: "150px", textAlign: "center" }}>
                    {new Intl.NumberFormat("en-IN", {
                      style: "decimal",
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(total)}
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </Box>

      <Box className="btn-container" sx={{ gap: 3 }}>
        <Button className="btn-outlined" onClick={handleBack}>
          Back
        </Button>
        <Button className="btn-contained" onClick={() => setOpen(true)}>
          Submit
        </Button>
      </Box>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Are you sure you want to submit these details?
          </Typography>
          <Box className="btn-container" sx={{ gap: 3, width: "100%" }}>
            <Button
              className="btn-outlined"
              variant="outlined"
              onClick={() => setOpen(false)}
            >
              No
            </Button>
            <Button
              className="btn-contained"
              onClick={handleNext}
              disabled={loading}
            >
              {loading ? "Submitting..." : "Yes"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default Preview;